(() => {
  const initialized = new WeakSet();
  const DEFAULT_MAX_CARDS = 200;
  const DECK_MATCH_TIMEOUT_MS = 12000;
  const DECK_MATCH_RETRY_DELAY_MS = 500;

  function text(tag, className, value) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    node.textContent = value ?? "";
    return node;
  }

  function setMessage(root, message, kind) {
    const target = root.querySelector("[data-deck-message]");
    if (!target) return;
    target.textContent = message;
    target.hidden = !message;
    target.className = "pokemon-deck-builder__message";
    if (kind) target.classList.add(`pokemon-deck-builder__message--${kind}`);
  }

  function updateCartBubble(sectionHtml) {
    const currentBubble = document.getElementById("cart-icon-bubble");
    if (!currentBubble || !sectionHtml) return false;

    const sectionDocument = new DOMParser().parseFromString(sectionHtml, "text/html");
    const renderedBubble = sectionDocument.getElementById("cart-icon-bubble");
    const sectionWrapper = sectionDocument.querySelector(
      "#shopify-section-cart-icon-bubble, .shopify-section"
    );
    const bubbleHtml = renderedBubble?.innerHTML || sectionWrapper?.innerHTML || sectionDocument.body.innerHTML;
    if (!bubbleHtml) return false;

    currentBubble.innerHTML = bubbleHtml;
    return true;
  }

  function money(root, value) {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return "Sin precio";
    try {
      return new Intl.NumberFormat("es-CL", {
        style: "currency",
        currency: root.dataset.currency || "CLP",
        maximumFractionDigits: 0
      }).format(Number(value));
    } catch {
      return String(value);
    }
  }

  function comparablePrice(value) {
    if (value === null || value === undefined || value === "") return Number.MAX_SAFE_INTEGER;
    const price = Number(value);
    return Number.isFinite(price) ? price : Number.MAX_SAFE_INTEGER;
  }

  function requestedCardCount(decklist) {
    return decklist.split(/\r?\n/).reduce((total, line) => {
      const match = line.trim().match(/^(\d+)x?\s+(.+)$/i);
      if (!match) return total;
      const quantity = Number(match[1]);
      return Number.isSafeInteger(quantity) && quantity > 0 ? total + quantity : total;
    }, 0);
  }

  function updateDeckLimit(root) {
    const input = root.querySelector("[data-deck-input]");
    const target = root.querySelector("[data-deck-limit]");
    const maxCards = Number(root.dataset.maxCards) || DEFAULT_MAX_CARDS;
    const requestedCards = requestedCardCount(input?.value || "");
    if (target) {
      target.textContent = `${requestedCards} de ${maxCards} cartas`;
      target.classList.toggle("pokemon-deck-builder__limit--exceeded", requestedCards > maxCards);
    }
    return { requestedCards, maxCards };
  }

  async function requestDeckMatch(apiUrl, body) {
    let lastError;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), DECK_MATCH_TIMEOUT_MS);
      try {
        const response = await fetch(`${apiUrl}/api/decklists/match`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
          signal: controller.signal
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          const responseError = new Error(payload.message || "No pudimos procesar la lista.");
          responseError.retryable = response.status === 408 || response.status === 429 || response.status >= 500;
          throw responseError;
        }
        return payload;
      } catch (error) {
        lastError = error;
        const retryable = error?.name === "AbortError" || error instanceof TypeError || error?.retryable;
        if (!retryable) throw error;
        if (attempt === 0) {
          await new Promise((resolve) => window.setTimeout(resolve, DECK_MATCH_RETRY_DELAY_MS));
        }
      } finally {
        window.clearTimeout(timeout);
      }
    }

    console.warn("El servicio del deck builder no está disponible.", lastError);
    throw new Error(
      "El constructor no está disponible temporalmente. Tu lista sigue aquí; espera un momento e intenta nuevamente."
    );
  }

  function renderSummary(root, summary) {
    const target = root.querySelector("[data-deck-summary]");
    if (!target) return;
    target.replaceChildren();
    const requested = Number(summary.requestedCards || 0);
    const found = Number(summary.foundCards ?? Math.max(0, requested - Number(summary.missingCards || 0)));
    const missing = Number(summary.missingCards ?? Math.max(0, requested - found));
    const items = [
      ["Solicitadas", requested, "requested"],
      ["Encontradas", found, "found"],
      ["No encontradas", missing, "missing"]
    ];
    items.forEach(([label, value, kind]) => {
      const card = text("div", `pokemon-deck-builder__summary-card pokemon-deck-builder__summary-card--${kind}`, "");
      card.append(text("strong", "pokemon-deck-builder__summary-value", String(value)));
      card.append(text("span", "pokemon-deck-builder__summary-label", label));
      target.append(card);
    });
    target.hidden = false;
  }

  function flattenVariants(line) {
    return (line.options || []).flatMap((option, optionIndex) =>
      (option.variants || []).map((variant, variantIndex) => ({ option, optionIndex, variant, variantIndex }))
    ).sort((left, right) =>
      comparablePrice(left.variant.price) - comparablePrice(right.variant.price) ||
      left.optionIndex - right.optionIndex ||
      left.variantIndex - right.variantIndex
    );
  }

  function renderVariant(root, entry, recommendedQuantity, isCheapest) {
    const { option, variant } = entry;
    const row = text("article", `pokemon-deck-builder__variant${isCheapest ? " pokemon-deck-builder__variant--cheapest" : ""}`, "");

    const media = text("button", "pokemon-deck-builder__variant-media", "");
    media.type = "button";
    media.dataset.cardPreview = "true";
    media.setAttribute("aria-label", `Ampliar imagen de ${option.title || option.cardName || "carta"}`);
    if (option.imageUrl) {
      const frame = text("span", "pokemon-deck-builder__variant-media-frame", "");
      const image = document.createElement("img");
      image.src = option.imageUrl;
      image.alt = option.title || option.cardName || "Carta";
      image.loading = "lazy";
      frame.append(image);

      const preview = text("span", "pokemon-deck-builder__variant-preview", "");
      preview.setAttribute("aria-hidden", "true");
      const previewImage = document.createElement("img");
      previewImage.src = option.imageUrl;
      previewImage.alt = "";
      previewImage.loading = "lazy";
      preview.append(previewImage);
      media.append(frame, preview);
    }

    const copy = text("div", "pokemon-deck-builder__variant-copy", "");
    const titleLink = text("a", "pokemon-deck-builder__variant-title", option.title || option.cardName || "Carta");
    titleLink.href = option.productUrl;
    copy.append(titleLink);
    const printing = [option.setName || option.setCode, option.cardNumber].filter(Boolean).join(" · ");
    if (printing) copy.append(text("span", "pokemon-deck-builder__variant-print", printing));
    const attributes = [variant.language, variant.condition, variant.finish].filter(Boolean).join(" · ");
    if (attributes) copy.append(text("span", "pokemon-deck-builder__variant-attributes", attributes));

    const commercial = text("div", "pokemon-deck-builder__variant-commercial", "");
    commercial.append(text("strong", "pokemon-deck-builder__price", money(root, variant.price)));
    commercial.append(text("span", "pokemon-deck-builder__stock", `${Math.max(0, Number(variant.stock) || 0)} disponibles`));

    const quantity = text("div", "pokemon-deck-builder__quantity", "");
    const down = text("button", "pokemon-deck-builder__quantity-button", "−");
    down.type = "button";
    down.dataset.quantityDirection = "down";
    down.setAttribute("aria-label", "Restar una unidad");
    const input = document.createElement("input");
    input.type = "number";
    input.min = "0";
    input.max = String(Math.max(0, Number(variant.stock) || 0));
    input.value = String(Math.max(0, Number(recommendedQuantity) || 0));
    input.inputMode = "numeric";
    input.dataset.cartQuantity = "true";
    input.dataset.cartVariant = variant.variantId;
    input.dataset.unitPrice = variant.price ?? "";
    input.setAttribute("aria-label", `Cantidad de ${option.title || option.cardName || "carta"}`);
    input.title = `Puedes seleccionar hasta ${Math.max(0, Number(variant.stock) || 0)} copias de esta variante`;
    const up = text("button", "pokemon-deck-builder__quantity-button", "+");
    up.type = "button";
    up.dataset.quantityDirection = "up";
    up.setAttribute("aria-label", "Agregar una unidad");
    quantity.append(down, input, up);

    const actions = text("div", "pokemon-deck-builder__variant-actions", "");
    const addButton = text("button", "pokemon-deck-builder__variant-add", "Agregar al carrito");
    addButton.type = "button";
    addButton.dataset.addDeckVariant = variant.variantId;
    addButton.disabled = Math.max(0, Number(recommendedQuantity) || 0) === 0;
    addButton.setAttribute("aria-label", `Agregar ${option.title || option.cardName || "carta"} al carrito`);
    actions.append(quantity, addButton);

    row.append(media, copy, commercial, actions);
    return row;
  }

  function renderLine(root, line) {
    const item = text("article", `pokemon-deck-builder__line pokemon-deck-builder__line--${line.status}`, "");
    item.dataset.deckLine = "true";
    item.dataset.requestedQuantity = String(line.requested.quantity);

    const header = text("header", "pokemon-deck-builder__line-header", "");
    const heading = text("div", "pokemon-deck-builder__line-heading", "");
    const print = [line.requested.setCode, line.requested.cardNumber].filter(Boolean).join(" ");
    heading.append(text("span", "pokemon-deck-builder__requested-label", `${line.requested.quantity} solicitadas`));
    heading.append(text("h3", "", line.requested.name));
    if (print) heading.append(text("span", "pokemon-deck-builder__requested-print", print));

    const lineMeta = text("div", "pokemon-deck-builder__line-meta", "");
    const status = line.status === "matched" ? "Completa" : line.status === "partial" ? "Parcial" : "No disponible";
    lineMeta.append(text("span", "pokemon-deck-builder__line-status", status));
    lineMeta.append(text("span", "pokemon-deck-builder__line-selected", `${line.allocatedQuantity} de ${line.requested.quantity} seleccionadas`));
    header.append(heading, lineMeta);
    item.append(header);

    const candidates = flattenVariants(line);
    if (candidates.length === 0) {
      item.append(text("p", "pokemon-deck-builder__empty-line", "No encontramos una impresión disponible para esta carta."));
      return item;
    }

    const recommendedByVariant = new Map((line.allocations || []).map((allocation) => [allocation.variantId, allocation.quantity]));
    const visible = candidates.slice(0, 1);
    const additional = candidates.slice(1);
    const variants = text("div", "pokemon-deck-builder__variants", "");
    visible.forEach((entry, index) => {
      variants.append(renderVariant(root, entry, recommendedByVariant.get(entry.variant.variantId) || 0, index === 0));
    });

    if (additional.length > 0) {
      const toggle = text("button", "pokemon-deck-builder__variants-toggle", `Ver ${additional.length} variante${additional.length === 1 ? "" : "s"} más`);
      toggle.type = "button";
      toggle.dataset.variantsToggle = "true";
      toggle.dataset.closedLabel = toggle.textContent;
      toggle.dataset.openLabel = "Ocultar variantes adicionales";
      toggle.setAttribute("aria-expanded", "false");
      const extra = text("div", "pokemon-deck-builder__variants-extra", "");
      extra.hidden = true;
      additional.forEach((entry) => {
        extra.append(renderVariant(root, entry, recommendedByVariant.get(entry.variant.variantId) || 0, false));
      });
      variants.append(toggle, extra);
    }

    item.append(variants);
    return item;
  }

  function isUnavailable(line) {
    return line.status === "missing" || flattenVariants(line).length === 0;
  }

  function renderMissingLine(line) {
    const quantity = Math.max(0, Number(line.requested.quantity) || 0);
    const print = [line.requested.setCode, line.requested.cardNumber].filter(Boolean).join(" ");
    const row = text("div", "pokemon-deck-builder__missing-row", "");
    row.append(text("strong", "pokemon-deck-builder__missing-quantity", `${quantity}×`));

    const card = text("span", "pokemon-deck-builder__missing-card", line.requested.name);
    card.title = [line.requested.name, print].filter(Boolean).join(" ");
    row.append(card);
    if (print) row.append(text("span", "pokemon-deck-builder__missing-print", print));
    row.append(text("span", "pokemon-deck-builder__missing-status", "No disponible"));
    return row;
  }

  function selectedCartItems(root) {
    const quantities = new Map();
    root.querySelectorAll("[data-cart-quantity]").forEach((input) => {
      const id = input.dataset.cartVariant;
      const quantity = Math.max(0, Number(input.value) || 0);
      if (id && quantity > 0) quantities.set(id, (quantities.get(id) || 0) + quantity);
    });
    return [...quantities].map(([id, quantity]) => ({ id, quantity }));
  }

  function updateLineSelection(line) {
    const requested = Math.max(0, Number(line.dataset.requestedQuantity) || 0);
    const selected = [...line.querySelectorAll("[data-cart-quantity]")]
      .reduce((sum, input) => sum + Math.max(0, Number(input.value) || 0), 0);
    const target = line.querySelector(".pokemon-deck-builder__line-selected");
    const extra = Math.max(0, selected - requested);
    if (target) {
      target.textContent = extra > 0
        ? `${selected} seleccionadas · ${extra} extra${extra === 1 ? "" : "s"}`
        : `${selected} de ${requested} seleccionadas`;
    }
    line.classList.toggle("pokemon-deck-builder__line--custom", selected !== requested);
    line.classList.toggle("pokemon-deck-builder__line--extra", extra > 0);
  }

  function updateBulkAction(root) {
    const bulk = root.querySelector("[data-deck-bulk]");
    const button = root.querySelector("[data-deck-add]");
    if (!bulk || !button) return;
    const inputs = [...root.querySelectorAll("[data-cart-quantity]")];
    const total = inputs.reduce((sum, input) => sum + Math.max(0, Number(input.value) || 0), 0);
    const estimatedPrice = inputs.reduce((sum, input) => {
      const quantity = Math.max(0, Number(input.value) || 0);
      const price = Number(input.dataset.unitPrice);
      return sum + (Number.isFinite(price) ? price * quantity : 0);
    }, 0);
    bulk.hidden = total === 0;
    button.disabled = total === 0;
    const detail = root.querySelector("[data-deck-bulk-detail]");
    if (detail) detail.textContent = `${total} carta${total === 1 ? "" : "s"} · ${money(root, estimatedPrice)}`;
  }

  function updateVariantAction(input) {
    const button = input.closest(".pokemon-deck-builder__variant")?.querySelector("[data-add-deck-variant]");
    if (button) button.disabled = Math.max(0, Number(input.value) || 0) === 0;
  }

  function normalizeQuantity(root, input, nextValue) {
    const line = input.closest("[data-deck-line]");
    if (!line) return;
    const stock = Math.max(0, Number(input.max) || 0);
    input.value = String(Math.min(stock, Math.max(0, Math.floor(Number(nextValue) || 0))));
    updateVariantAction(input);
    updateLineSelection(line);
    updateBulkAction(root);
  }

  function renderResult(root, payload) {
    renderSummary(root, payload.summary);
    const target = root.querySelector("[data-deck-results]");
    if (!target) return;
    const lines = Array.isArray(payload.lines) ? payload.lines : [];
    const available = lines.filter((line) => !isUnavailable(line));
    const unavailable = lines.filter(isUnavailable);
    const content = available.map((line) => renderLine(root, line));

    if (unavailable.length > 0) {
      const group = text("section", "pokemon-deck-builder__missing-group", "");
      const heading = text("div", "pokemon-deck-builder__missing-heading", "");
      heading.append(text("strong", "", "No disponibles"));
      heading.append(text("span", "", `${unavailable.length} carta${unavailable.length === 1 ? "" : "s"}`));
      group.append(heading);
      unavailable.forEach((line) => group.append(renderMissingLine(line)));
      content.push(group);
    }

    target.replaceChildren(...content);
    target.hidden = false;
    target.querySelectorAll("[data-deck-line]").forEach(updateLineSelection);
    updateBulkAction(root);
  }

  async function submitDeck(root) {
    const input = root.querySelector("[data-deck-input]");
    const button = root.querySelector("[data-deck-submit]");
    const apiUrl = (root.dataset.apiUrl || "").replace(/\/+$/, "");
    if (!apiUrl) throw new Error("Falta configurar la URL de Filtros Singles CC en esta sección.");
    if (!input?.value.trim()) {
      const listName = (root.dataset.game || "pokemon_en") === "magic" ? "Magic" : "Limitless";
      throw new Error(`Pega una lista de ${listName} antes de buscar.`);
    }
    const { requestedCards, maxCards } = updateDeckLimit(root);
    if (requestedCards > maxCards) {
      throw new Error(`La lista contiene más de ${maxCards} cartas. Divídela en búsquedas más pequeñas.`);
    }

    button.disabled = true;
    setMessage(root, "Buscando cartas y variantes disponibles...", "");
    try {
      const payload = await requestDeckMatch(apiUrl, {
        decklist: input.value,
        game: root.dataset.game || "pokemon_en"
      });
      renderResult(root, payload);
      setMessage(root, `Encontramos ${payload.summary.foundCards} de ${payload.summary.requestedCards} cartas.`, "success");
    } finally {
      button.disabled = false;
    }
  }

  async function addCartItems(root, items, button, pendingMessage, successMessage) {
    const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
    const sections = typeof cart?.getSectionsToRender === "function"
      ? cart.getSectionsToRender().map((section) => section.id)
      : ["cart-icon-bubble"];
    button.disabled = true;
    setMessage(root, pendingMessage, "");
    try {
      const response = await fetch(root.dataset.cartAddUrl || "/cart/add.js", {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.description || "Shopify no pudo agregar las cartas.");
      }

      if (cart && typeof cart.renderContents === "function" && payload.sections) {
        try {
          if (typeof cart.setActiveElement === "function") cart.setActiveElement(button);
          cart.classList.remove("is-empty");
          cart.renderContents(payload);
        } catch (error) {
          console.warn("No pudimos renderizar el carrito del theme; se actualizara el contador.", error);
        }
      }
      updateCartBubble(payload.sections?.["cart-icon-bubble"]);
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "pokemon-deck-builder",
          cartData: payload,
          variantId: items[0]?.id
        });
      }
      setMessage(root, successMessage, "success");
    } finally {
      button.disabled = false;
    }
  }

  async function addSelected(root) {
    const items = selectedCartItems(root);
    if (!items.length) throw new Error("Selecciona al menos una carta para agregar.");

    const button = root.querySelector("[data-deck-add]");
    await addCartItems(
      root,
      items,
      button,
      "Agregando la selección al carrito...",
      "Cartas agregadas al carrito."
    );
  }

  async function addVariant(root, button) {
    const row = button.closest(".pokemon-deck-builder__variant");
    const input = row?.querySelector("[data-cart-quantity]");
    if (!input) throw new Error("No pudimos identificar esta carta.");

    normalizeQuantity(root, input, input.value);
    const id = input.dataset.cartVariant;
    const quantity = Math.max(0, Number(input.value) || 0);
    if (!id || quantity === 0) throw new Error("Selecciona al menos una copia para agregar.");

    await addCartItems(
      root,
      [{ id, quantity }],
      button,
      `Agregando ${quantity} carta${quantity === 1 ? "" : "s"} al carrito...`,
      `${quantity} carta${quantity === 1 ? "" : "s"} agregada${quantity === 1 ? "" : "s"} al carrito.`
    );
    updateVariantAction(input);
  }

  function init(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    const input = root.querySelector("[data-deck-input]");
    const results = root.querySelector("[data-deck-results]");
    input?.addEventListener("input", () => updateDeckLimit(root));
    updateDeckLimit(root);

    root.querySelector("[data-deck-form]")?.addEventListener("submit", (event) => {
      event.preventDefault();
      submitDeck(root).catch((error) => setMessage(root, error.message || String(error), "error"));
    });
    root.querySelector("[data-deck-add]")?.addEventListener("click", () => {
      addSelected(root).catch((error) => setMessage(root, error.message || String(error), "error"));
    });
    results?.addEventListener("click", (event) => {
      const addVariantButton = event.target.closest("[data-add-deck-variant]");
      if (addVariantButton) {
        addVariant(root, addVariantButton).catch((error) => setMessage(root, error.message || String(error), "error"));
        return;
      }

      const cardPreview = event.target.closest("[data-card-preview]");
      if (cardPreview) {
        root.querySelectorAll("[data-card-preview].is-preview-open").forEach((preview) => {
          if (preview !== cardPreview) preview.classList.remove("is-preview-open");
        });
        cardPreview.classList.toggle("is-preview-open");
        return;
      }

      const quantityButton = event.target.closest("[data-quantity-direction]");
      if (quantityButton) {
        const quantityInput = quantityButton.parentElement?.querySelector("[data-cart-quantity]");
        if (quantityInput) {
          const delta = quantityButton.dataset.quantityDirection === "up" ? 1 : -1;
          normalizeQuantity(root, quantityInput, Number(quantityInput.value) + delta);
        }
        return;
      }

      const toggle = event.target.closest("[data-variants-toggle]");
      if (toggle) {
        const extra = toggle.nextElementSibling;
        const opening = extra?.hidden;
        if (extra) extra.hidden = !opening;
        toggle.setAttribute("aria-expanded", String(Boolean(opening)));
        toggle.textContent = opening ? toggle.dataset.openLabel : toggle.dataset.closedLabel;
      }
    });
    results?.addEventListener("change", (event) => {
      const quantityInput = event.target.closest("[data-cart-quantity]");
      if (quantityInput) normalizeQuantity(root, quantityInput, quantityInput.value);
    });
  }

  function initAll(scope = document) {
    scope.querySelectorAll("[data-pokemon-deck-builder]").forEach(init);
  }

  document.addEventListener("DOMContentLoaded", () => initAll());
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
